import type { IServices } from '@r1-frontend/api-domru/api-profile/v2/info/all/dto/ResponseDto';
import { IGetConnectPriceRequestDto } from '@r1-frontend/api-domru/api-request/v1/get-connect-price';

import { ILabelProps } from '@r1-frontend/ui-react/components/label';
import { TOption } from '@r1-frontend/ui-react/components/OptionList';
import { InfoCircleFlip } from '@r1-frontend/ui-react/components/svg/main';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';

import { PRODUCTS } from '~/src/constants';
import { EPriceOperationTypes, IPriceOptionProps } from '~/src/entities/price';
import type { PriceOptionProps } from '~/src/features/work-in-home-ab/types';

import { OldValue } from './styled';

export interface ISelectedElement {
    optionId: string | number,
    variantId: string | number,
}

export const getDeliveryOptions = (
    installPrice: number,
    deliveryPrice: number,
    isAvailablePost: boolean,
    isEngineerDeliveryAvailable: boolean,
): TOption[] => {
    const postItem = {
        id: 'post',
        label: 'Пункт выдачи Почта России',
        valueStr: '0 ₽',
        value: 0,
    };

    const engineerItem = {
        id: 'engineer',
        variants: [
            {
                id: 'installation',
                label: 'Доставка и настройка инженером',
                valueStr: `${installPrice} ₽`,
                value: installPrice,
            },
            {
                id: 'delivery',
                label: 'Доставка инженером',
                valueStr: `${deliveryPrice} ₽`,
                value: deliveryPrice,
            },
        ],
    };

    if (isAvailablePost && isEngineerDeliveryAvailable) {
        return [postItem, engineerItem];
    }
    if (isAvailablePost && !isEngineerDeliveryAvailable) {
        return [postItem];
    }
    if (!isAvailablePost && isEngineerDeliveryAvailable) {
        return [engineerItem];
    }
    return [];
};

const createChip = (props: PriceOptionProps | IPriceOptionProps): ILabelProps | undefined =>
    props?.requestId
        ? {
              type: 'LabelsText_2',
              children:
                <>
                    {'акция'}
                    <InfoCircleFlip size={16} />
                </>,
              outlined: true,
          }
        : undefined;

export const getPayOptions = (priceOptions: (PriceOptionProps | IPriceOptionProps)[]): TOption[] => {
    const saleOption = priceOptions.find(el => el.operType === EPriceOperationTypes.Possess);

    const leasingOptions = priceOptions
        .filter(el => el.operType === EPriceOperationTypes.Leasing)
        .map(el => ({
            id: String(el.value),
            label: el.label,
            value: String(el.value),
            valueStr: `${el.price} ₽/мес`,
            chip: createChip(el),
        }));

    const pricePromo = saleOption?.pricePromo;
    const res: TOption[] = saleOption?.price
        ? [
            {
                id: EPriceOperationTypes.Possess,
                label: 'Полная стоимость',
                value: pricePromo || saleOption?.value || 0,
                valueStr: `${pricePromo || saleOption?.price} ₽`,
                chip: createChip(saleOption),
                additional: pricePromo
                    ? <OldValue>{saleOption?.value || saleOption?.price}{RubleSymbol}</OldValue>
                    : undefined,
            },
        ]
        : [];

    if (leasingOptions.length > 0) {
        res.push({
            id: EPriceOperationTypes.Leasing,
            variants: leasingOptions,
        });
    }
    return res;
};

/**
 * Проверяет подключен ли нужный продукт
 */
const checkRequiredService = (services: IServices[] | undefined, neededServiceId: number) => {
    if (neededServiceId > 0) {
        if (Array.isArray(services)) {
            const findProduct = services.find(el => el.productId === neededServiceId);
            return !!findProduct;
        }
        return false;
    }

    return true;
};

interface ICheckAvailableParams {
    services: IServices[] | undefined,
    neededProductId: number,
    leftovers: number,
    showForDelivery: boolean,
}

/**
 * Проверить доступность доставки почтой
 */
export const checkAvailableDeliveryByPost = ({
    services,
    neededProductId,
    leftovers,
    showForDelivery,
}: ICheckAvailableParams): boolean => {
    const isCheck = checkRequiredService(services, neededProductId);
    return showForDelivery && leftovers > 0 && isCheck;
};

export const getProductNeedsParams = (productId: number): Omit<IGetConnectPriceRequestDto, 'int100' | 'install'> => {
    switch (productId) {
        case PRODUCTS.internet:
            return {
                needTv: 0,
                needInt: 1,
            };
        case PRODUCTS.tv:
            return {
                needTv: 1,
                needInt: 0,
            };
        default:
            return {
                needTv: 0,
                needInt: 0,
            };
    }
};
